/**
 * Asset Name Cell Renderer
 *
 * Shows appropriate icon for seed-sourced assets.
 * Prepends ClockIcon when the row has a pending bulk action (UX divergence #2/#6).
 */

import { ClockIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Seeds as SeedsIcon } from '@/components/chariot-ui';
import { NameColumn } from '@/components/NameColumn';
import { useRowPendingState } from '@/hooks/useRowPendingState';
import type { Asset } from '@/types';
import { getDisplayIdentifierWithFallback } from '@/utils/displayIdentifier.util';

interface AssetNameCellProps {
  asset: Asset;
}

/**
 * Memoized cell renderer for asset name column to improve performance
 *
 * Field priority is clearly documented in getDisplayIdentifierWithFallback function.
 *
 * @param asset - The asset to render
 * @returns NameColumn component with appropriate identifier and icon
 */
export const AssetNameCell: React.FC<AssetNameCellProps> = React.memo(
  ({ asset }) => {
    const isPending = useRowPendingState(asset.key);
    const displayName = getDisplayIdentifierWithFallback(asset, '');

    const icon = isPending ? (
      // UX divergence #2: ClockIcon prepended when pending bulk action — redundant
      // visual signal alongside opacity-70 and aria-busy (WCAG 1.4.1 Use of Color).
      <ClockIcon className="text-chariot-text-secondary mr-2 size-4 shrink-0" />
    ) : asset.source === 'seed' ? (
      <SeedsIcon className="size-4" />
    ) : undefined;

    return (
      <NameColumn name={displayName} description={asset.group} icon={icon} />
    );
  }
);

AssetNameCell.displayName = 'AssetNameCell';
